import { SearchLg as SearchIcon } from "@untitledui/icons";
import type {
  FocusEventHandler,
  PointerEventHandler,
  RefAttributes,
  RefObject,
} from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type {
  ComboBoxProps as AriaComboBoxProps,
  GroupProps as AriaGroupProps,
  ListBoxProps as AriaListBoxProps,
} from "react-aria-components";
import {
  ComboBox as AriaComboBox,
  Group as AriaGroup,
  Input as AriaInput,
  ListBox as AriaListBox,
  ComboBoxStateContext,
} from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import {
  type CommonProps,
  SelectContext,
  type SelectItemType,
  sizes,
} from "@/components/base/select/select";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";

interface ComboBoxProps
  extends Omit<AriaComboBoxProps<SelectItemType>, "children" | "items">,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  shortcut?: boolean;
  items?: SelectItemType[];
  popoverClassName?: string;
  shortcutClassName?: string;
  children: AriaListBoxProps<SelectItemType>["children"];
}

interface ComboBoxValueProps extends AriaGroupProps {
  size: "sm" | "md";
  shortcut: boolean;
  placeholder?: string;
  shortcutClassName?: string;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
  ref?: RefObject<HTMLDivElement | null>;
  inputId?: string;
  inputName?: string;
}

const ComboBoxValue = ({
  size,
  shortcut,
  placeholder,
  shortcutClassName,
  inputId,
  inputName,
  ...otherProps
}: ComboBoxValueProps) => {
  const state = useContext(ComboBoxStateContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const rejectFocusRef = useRef(false);
  const selectedKeyRef = useRef(state?.selectedKey ?? null);

  const value = state?.selectedItem?.value || null;
  const inputValue = state?.inputValue || null;

  const first = inputValue?.split(value?.supportingText)?.[0] || "";
  const last = inputValue?.split(first)[1];

  const selectedKey = state?.selectedKey ?? null;

  useEffect(() => {
    if (selectedKey !== null && selectedKey !== selectedKeyRef.current) {
      const input = inputRef.current;
      const focusContainer = input?.closest<HTMLElement>('[role="dialog"]');

      if (focusContainer) {
        focusContainer.focus();
      } else {
        rejectFocusRef.current = true;
        input?.blur();
      }
    }

    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  return (
    <AriaGroup
      {...otherProps}
      onPointerDown={() => {
        rejectFocusRef.current = false;

        if (!state?.isOpen) {
          state?.open(null, "manual");
        }
      }}
      className={({ isFocusWithin, isDisabled }) =>
        cx(
          "relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition-shadow duration-100 ease-linear ring-inset",
          isDisabled && "cursor-not-allowed bg-disabled_subtle",
          isFocusWithin && "ring-2 ring-brand",
          sizes[size].root,
        )
      }
    >
      {({ isDisabled, isFocusWithin }) => (
        <>
          <SearchIcon className="pointer-events-none size-5 text-fg-quaternary" />

          <div className="relative flex w-full items-center gap-2">
            {inputValue && !isFocusWithin && (
              <span
                className="absolute top-1/2 z-0 inline-flex w-full -translate-y-1/2 gap-2 truncate"
                aria-hidden="true"
              >
                <p
                  className={cx(
                    "text-md font-medium text-primary",
                    isDisabled && "text-disabled",
                  )}
                >
                  {first}
                </p>
                {last && (
                  <p
                    className={cx(
                      "-ml-0.75 text-md text-tertiary",
                      isDisabled && "text-disabled",
                    )}
                  >
                    {last}
                  </p>
                )}
              </span>
            )}

            <AriaInput
              ref={inputRef}
              id={inputId}
              name={inputName}
              placeholder={placeholder}
              onFocus={(e) => {
                if (rejectFocusRef.current) {
                  if (e.relatedTarget === null) {
                    e.currentTarget.blur();
                    return;
                  }

                  rejectFocusRef.current = false;
                }

                e.currentTarget.select();
              }}
              className={cx(
                "z-10 w-full appearance-none bg-transparent text-md caret-alpha-black/90 placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled",
                isFocusWithin ? "text-primary" : "text-transparent",
              )}
            />
          </div>

          {shortcut && (
            <div
              className={cx(
                "absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8",
                isDisabled && "to-bg-disabled_subtle",
                sizes[size].shortcut,
                shortcutClassName,
              )}
            >
              <span
                className={cx(
                  "pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset",
                  isDisabled && "bg-transparent text-disabled",
                )}
                aria-hidden="true"
              >
                ⌘K
              </span>
            </div>
          )}
        </>
      )}
    </AriaGroup>
  );
};

export const ComboBox = ({
  placeholder = "Search",
  shortcut = true,
  size = "sm",
  children,
  items,
  shortcutClassName,
  id,
  name,
  ...otherProps
}: ComboBoxProps) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState("");

  // Resize observer for popover width
  const onResize = useCallback(() => {
    if (!placeholderRef.current) return;

    const divRect = placeholderRef.current?.getBoundingClientRect();

    setPopoverWidth(`${divRect.width}px`);
  }, []);

  useResizeObserver({
    ref: placeholderRef,
    box: "border-box",
    onResize,
  });

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaComboBox
        aria-label={otherProps.label || placeholder}
        {...otherProps}
      >
        {(state) => (
          <div className="flex flex-col gap-1.5">
            {otherProps.label && (
              <Label isRequired={state.isRequired} tooltip={otherProps.tooltip} className="w-fit">
                {otherProps.label}
              </Label>
            )}

            <ComboBoxValue
              ref={placeholderRef}
              placeholder={placeholder}
              shortcut={shortcut}
              shortcutClassName={shortcutClassName}
              size={size}
              inputId={id}
              inputName={name}
              // This is a workaround to correctly calculating the trigger width
              // while using ResizeObserver wasn't 100% reliable.
              onFocus={onResize}
              onPointerEnter={onResize}
            />

            <Popover
              size={size}
              triggerRef={placeholderRef}
              style={{ width: popoverWidth }}
              className={otherProps.popoverClassName}
            >
              <AriaListBox items={items} className="size-full outline-hidden">
                {children}
              </AriaListBox>
            </Popover>

            {otherProps.hint && (
              <HintText isInvalid={state.isInvalid}>{otherProps.hint}</HintText>
            )}
          </div>
        )}
      </AriaComboBox>
    </SelectContext.Provider>
  );
};
